import { useState, useEffect } from "react";
import { supabase, isSupabaseEnabled } from "../lib/supabase";

// Module-level cache: cacheKey = "flagName:userEmail" → boolean
const cache = new Map<string, boolean>();

/**
 * Returns true if the named feature flag is enabled for the given user.
 *
 * Rules:
 *   enabled=true  + user_emails is empty  → on for everyone
 *   enabled=true  + user_emails has items → on only if userEmail is in the list
 *   enabled=false or flag not found        → false
 *
 * Results are cached in a module-level Map so repeated calls don't re-fetch
 * within the same browser session.
 */
export function useFeatureFlag(flagName: string, userEmail?: string): boolean {
  const cacheKey = `${flagName}:${userEmail ?? ""}`;
  const [enabled, setEnabled] = useState<boolean>(cache.get(cacheKey) ?? false);

  useEffect(() => {
    if (!isSupabaseEnabled() || !supabase) return;

    void (async () => {
      // Serve from cache if present. Checked inside the async callback (not the
      // effect body) so it's not a synchronous setState-in-effect, and it still
      // syncs if another consumer populated the cache between render and effect.
      if (cache.has(cacheKey)) {
        setEnabled(cache.get(cacheKey)!);
        return;
      }
      try {
        const { data } = await supabase
          .from("feature_flags")
          .select("enabled, user_emails")
          .eq("flag", flagName)
          .maybeSingle();
        if (!data || !data.enabled) {
          cache.set(cacheKey, false);
          setEnabled(false);
          return;
        }
        const emails: string[] = data.user_emails ?? [];
        const result =
          emails.length === 0 ||
          (!!userEmail && emails.includes(userEmail));
        cache.set(cacheKey, result);
        setEnabled(result);
      } catch {
        cache.set(cacheKey, false);
        setEnabled(false);
      }
    })();
  }, [flagName, userEmail, cacheKey]);

  return enabled;
}
